import type React from 'react';
import {
  X,
  Download,
  ExternalLink,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  FlipHorizontal,
  Check,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PdfPages } from './PdfPages';
import { documentsService } from '../../services/documentsService';
import {
  type Orientation,
  type ViewState,
  orientationOf,
  sameOrientation,
  transformFor,
  turn,
  zoom,
} from './orientation';

type PDFViewerProps = {
  url: string;
  /**
   * JIM-1007-01 (Jim 2026-10-07): with the document's id, a PDF is fetched
   * through the API and drawn page by page — a PDF framed by its URL shows
   * nothing on a phone. The URL stays as the open-in-new-tab fallback.
   */
  documentId?: string | null;
  title: string;
  onClose: () => void;
  onDownload?: () => void;
  /** Drives <img> vs <iframe>; the Documents tab holds photos too. */
  mimeType?: string | null;
  fileName?: string | null;
  /**
   * Jim 11/2: inspectors zoom, turn and flip the BMP maps they upload and
   * can save the way-up; outside viewers get nothing but a view — no open-
   * in-new-tab, no download, no controls, no context menu.
   */
  tools?: 'inspector' | 'view-only';
  /** The saved way-up, applied on open. */
  orientation?: Orientation | null;
  onSaveOrientation?: (o: Orientation) => Promise<unknown> | void;
};

/** HEIC/HEIF upload fine from an iPhone but no browser paints them — they
 *  fall through to the "open or download" fallback rather than a dead frame. */
const RENDERABLE_IMAGE = /^image\/(jpeg|png|gif|webp|bmp|svg\+xml)$/;

function isImage(mimeType?: string | null, fileName?: string | null): boolean {
  if (mimeType && RENDERABLE_IMAGE.test(mimeType)) return true;
  if (mimeType?.startsWith('image/')) return false; // HEIC & friends
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(fileName ?? '');
}

function isPdf(mimeType?: string | null, fileName?: string | null): boolean {
  return mimeType === 'application/pdf' || /\.pdf$/i.test(fileName ?? '');
}

export function PDFViewer({
  url,
  documentId,
  title,
  onClose,
  onDownload,
  mimeType,
  fileName,
  tools,
  orientation,
  onSaveOrientation,
}: PDFViewerProps) {
  const viewOnly = tools === 'view-only';
  const saved = orientationOf(orientation);
  const [view, setView] = useState<ViewState>({ ...saved, scale: 1 });
  const [saving, setSaving] = useState(false);
  const changed = !sameOrientation(view, saved);
  const saveOrientation = async () => {
    if (!onSaveOrientation) return;
    setSaving(true);
    try {
      await onSaveOrientation({ rotation: view.rotation, flip: view.flip });
    } finally {
      setSaving(false);
    }
  };
  const stopMenu = viewOnly
    ? (e: React.SyntheticEvent) => e.preventDefault()
    : undefined;
  const drawPages = !!documentId && isPdf(mimeType, fileName);
  // An image the browser cannot paint (HEIC straight off an iPhone) goes
  // straight to the open/download fallback instead of a dead frame.
  const unpaintable =
    !!mimeType?.startsWith('image/') && !isImage(mimeType, fileName);
  const [isLoading, setIsLoading] = useState(!unpaintable && !drawPages);
  const [error, setError] = useState(false);

  // JIM-1014-18: on a phone the browser's toolbar shifts the visible area (the visual viewport)
  // below the layout viewport a "fixed" box is pinned to — the header and close button sat 44px
  // above the screen. The viewer follows the visible area and the page behind it stops scrolling.
  const [visible, setVisible] = useState<{
    top: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    const body = document.body;
    const before = body.style.overflow;
    body.style.overflow = 'hidden';
    const vv = window.visualViewport;
    const follow = () =>
      vv && setVisible({ top: vv.offsetTop, height: vv.height });
    follow();
    vv?.addEventListener('resize', follow);
    vv?.addEventListener('scroll', follow);
    return () => {
      body.style.overflow = before;
      vv?.removeEventListener('resize', follow);
      vv?.removeEventListener('scroll', follow);
    };
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError(true);
  };

  const handleOpenInNewTab = () => {
    window.open(url, '_blank');
  };

  // JIM-1014-18 (Jim 2026-10-14): rendered at the top of the document. Inside the Lite site
  // page a scrolled ancestor became the "fixed" box's frame, so on a phone the header — and the
  // close button — sat above the screen (y = -35 on staging). Above the Test banner (z-[200]); the
  // header clears the notch; the page area may shrink (min-h-0) so a long PDF scrolls in the viewer.
  return createPortal(
    <div
      className="fixed inset-0 z-[250] bg-black/80 flex flex-col"
      style={
        visible
          ? { top: visible.top, height: visible.height, bottom: 'auto' }
          : undefined
      }
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 pb-2 bg-slate-900 border-b border-slate-700"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
      >
        <h2 className="text-white font-medium truncate flex-1 mr-4">{title}</h2>
        <div className="flex items-center gap-2">
          {!viewOnly && (
            <button
              onClick={handleOpenInNewTab}
              className="p-3 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
          )}
          {onDownload && !viewOnly && (
            <button
              onClick={onDownload}
              className="p-3 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-3 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            title="Close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {tools === 'inspector' && (
        <div
          data-testid="viewer-tools"
          className="flex items-center gap-1 px-2 py-1 bg-slate-800 border-b border-slate-700 overflow-x-auto"
        >
          <ToolButton
            label="Zoom in"
            onClick={() => setView((v) => zoom(v, 'in'))}
          >
            <ZoomIn className="w-5 h-5" />
          </ToolButton>
          <ToolButton
            label="Zoom out"
            onClick={() => setView((v) => zoom(v, 'out'))}
          >
            <ZoomOut className="w-5 h-5" />
          </ToolButton>
          <span className="text-xs text-slate-400 tabular-nums w-10 text-center">
            {Math.round(view.scale * 100)}%
          </span>
          <ToolButton
            label="Turn left"
            onClick={() => setView((v) => turn(v, 'left'))}
          >
            <RotateCcw className="w-5 h-5" />
          </ToolButton>
          <ToolButton
            label="Turn right"
            onClick={() => setView((v) => turn(v, 'right'))}
          >
            <RotateCw className="w-5 h-5" />
          </ToolButton>
          <ToolButton
            label="Flip"
            onClick={() => setView((v) => ({ ...v, flip: !v.flip }))}
          >
            <FlipHorizontal className="w-5 h-5" />
          </ToolButton>
          {onSaveOrientation && changed && (
            <button
              type="button"
              onClick={saveOrientation}
              disabled={saving}
              data-testid="save-orientation"
              className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
            >
              <Check className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save this way up'}
            </button>
          )}
        </div>
      )}

      {/* PDF Content */}
      <div className="flex-1 min-h-0 relative" onContextMenu={stopMenu}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              <p className="text-slate-400">Loading document...</p>
            </div>
          </div>
        )}

        {error || unpaintable ? (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
            <div className="flex flex-col items-center gap-4 text-center px-4">
              <p className="text-slate-300">
                {unpaintable
                  ? 'This image format has no browser preview — open or download it to view.'
                  : 'Unable to display this document in the browser.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleOpenInNewTab}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Open in New Tab
                </button>
                {onDownload && !viewOnly && (
                  <button
                    onClick={onDownload}
                    className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    Download
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : isImage(mimeType, fileName) ? (
          <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
            <img
              src={url}
              alt={title}
              draggable={false}
              data-testid="viewer-image"
              className={`max-w-full max-h-full object-contain transition-transform ${viewOnly ? 'select-none' : ''}`}
              style={{ transform: transformFor(view) }}
              onLoad={handleLoad}
              onError={handleError}
            />
          </div>
        ) : drawPages ? (
          <div className="w-full h-full overflow-auto">
            <div
              data-testid="viewer-pages"
              style={{
                transform: transformFor(view),
                transformOrigin: 'top center',
              }}
            >
              <PdfPages
                load={() => documentsService.fetchDocumentBytes(documentId!)}
                title={title}
                reloadKey={documentId}
                fallbackUrl={viewOnly ? undefined : url}
              />
            </div>
          </div>
        ) : (
          <iframe
            src={url}
            className="w-full h-full border-0"
            title={title}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </div>
    </div>,
    document.body
  );
}

function ToolButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700"
    >
      {children}
    </button>
  );
}
